import React from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="font-sans">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="text-gray-600 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft className="w-6 h-6 relative mb-[2px]" />
          </button>
          <h1 className="text-xl font-bold">{t("termsPage.title")}</h1>
        </div>
      </header>

      {/* Content */}
      <div className="bg-white dark:bg-transparent max-w-2xl mx-auto px-4 py-6 space-y-6 text-gray-700 dark:text-gray-300">
        <p className="text-xs text-gray-500 dark:text-gray-400">{t("termsPage.effectiveDate")}</p>
        <p>{t("termsPage.intro")}</p>

        <Section title={t("termsPage.s1Title")}>
          <p>{t("termsPage.s1Body")}</p>
        </Section>

        <Section title={t("termsPage.s2Title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("termsPage.s2Item1")}</li>
            <li>{t("termsPage.s2Item2")}</li>
            <li>{t("termsPage.s2Item3")}</li>
            <li>{t("termsPage.s2Item4")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s3Title")}>
          <p>{t("termsPage.s3Lead")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-2">
            <li>{t("termsPage.s3Item1")}</li>
            <li>{t("termsPage.s3Item2")}</li>
            <li>{t("termsPage.s3Item3")}</li>
          </ul>
          <p className="mt-2">{t("termsPage.s3Footer")}</p>
        </Section>

        <Section title={t("termsPage.s4Title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("termsPage.s4Item1")}</li>
            <li>{t("termsPage.s4Item2")}</li>
            <li>{t("termsPage.s4Item3")}</li>
            <li>{t("termsPage.s4Item4")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s5Title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("termsPage.s5Item1")}</li>
            <li>{t("termsPage.s5Item2")}</li>
            <li>{t("termsPage.s5Item3")}</li>
            <li>{t("termsPage.s5Item4")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s6Title")}>
          <p className="font-medium">{t("termsPage.s6ByUsersLabel")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>{t("termsPage.s6Item1")}</li>
            <li>{t("termsPage.s6Item2")}</li>
            <li>{t("termsPage.s6Item3")}</li>
          </ul>
          <p className="font-medium mt-3">{t("termsPage.s6ByKpLabel")}</p>
          <p className="mt-1">{t("termsPage.s6CancelLead")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>{t("termsPage.s6CItem1")}</li>
            <li>{t("termsPage.s6CItem2")}</li>
            <li>{t("termsPage.s6CItem3")}</li>
            <li>{t("termsPage.s6CItem4")}</li>
          </ul>
          <p className="font-medium mt-3">{t("termsPage.s6RefundLabel")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>{t("termsPage.s6RefundItem1")}</li>
            <li>{t("termsPage.s6RefundItem2")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s7Title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("termsPage.s7Item1")}</li>
            <li>{t("termsPage.s7Item2")}</li>
            <li>{t("termsPage.s7Item3")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s8Title")}>
          <p>{t("termsPage.s8Lead")}</p>
          <ul className="list-disc pl-5 space-y-1 mt-1">
            <li>{t("termsPage.s8Item1")}</li>
            <li>{t("termsPage.s8Item2")}</li>
            <li>{t("termsPage.s8Item3")}</li>
            <li>{t("termsPage.s8Item4")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s9Title")}>
          <p>{t("termsPage.s9Body")}</p>
        </Section>

        <Section title={t("termsPage.s10Title")}>
          <ul className="list-disc pl-5 space-y-1">
            <li>{t("termsPage.s10Item1")}</li>
            <li>{t("termsPage.s10Item2")}</li>
            <li>{t("termsPage.s10Item3")}</li>
          </ul>
        </Section>

        <Section title={t("termsPage.s11Title")}>
          <p>{t("termsPage.s11Body")}</p>
        </Section>

        <Section title={t("termsPage.s12Title")}>
          <p>{t("termsPage.s12Body")}</p>
        </Section>

        <Section title={t("termsPage.s13Title")}>
          <p>{t("termsPage.s13Body")}</p>
        </Section>

        <Section title={t("termsPage.s14Title")}>
          <p>
            {t("termsPage.emailLabel")}{" "}
            <a
              href="mailto:support@worldcreatorventures.com"
              className="text-primary-100 underline"
            >
              support@worldcreatorventures.com
            </a>
          </p>
        </Section>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div>
    <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-2">{title}</h2>
    <div className="text-sm space-y-1">{children}</div>
  </div>
);

export default TermsAndConditionsPage;
